'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';

interface HolidaySearchFormProps {
  onSearch: (params: any) => void;
}

const HolidaySearchForm = ({ onSearch }: HolidaySearchFormProps) => {
  const [destination, setDestination] = useState('');
  const [duration, setDuration] = useState('7');
  const [budget, setBudget] = useState('medium');
  const [travelers, setTravelers] = useState(2);
  const [departureDate, setDepartureDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const searchParams = { destination, duration, budget, travelers, departureDate };
    
    trackEvent('holiday_search', {
      destination,
      duration,
      budget,
      travelers,
    });
    
    onSearch(searchParams);
  };

  return (
    <div className="bg-card rounded-xl shadow-3d p-6 lg:p-8 transform-3d shine-effect border-4 border-accent/20">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Destination */}
          <div className="relative">
            <label className="block text-sm font-black text-foreground mb-2">
              Destination
            </label>
            <div className="relative">
              <Icon name="GlobeAltIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Where do you want to go?"
                className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg text-foreground"
                required
              />
            </div>
          </div>

          {/* Duration and Budget */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-black text-foreground mb-2">
                Duration
              </label>
              <div className="relative">
                <Icon name="ClockIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg appearance-none text-foreground"
                >
                  <option value="3">3-5 Days</option>
                  <option value="7">7-10 Days</option>
                  <option value="14">2 Weeks</option>
                  <option value="21">3+ Weeks</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-black text-foreground mb-2">
                Budget
              </label>
              <div className="relative">
                <Icon name="CurrencyDollarIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg appearance-none text-foreground"
                >
                  <option value="budget">Budget ($500-$1500)</option>
                  <option value="medium">Mid-Range ($1500-$3500)</option>
                  <option value="luxury">Luxury ($3500+)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Travelers */}
          <div className="relative">
            <label className="block text-sm font-black text-foreground mb-2">
              Travelers
            </label>
            <div className="relative">
              <Icon name="UserGroupIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <select
                value={travelers}
                onChange={(e) => setTravelers(Number(e.target.value))}
                className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg appearance-none text-foreground"
              >
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <option key={num} value={num}>{num} Traveler{num > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Button */}
          <button
            type="submit"
            className="w-full px-8 py-4 gradient-gold-shiny text-primary font-heading font-black text-lg rounded-lg shadow-3d transition-all duration-300 hover:scale-105 active:scale-95 shine-effect border-2 border-accent flex items-center justify-center gap-2"
          >
            <Icon name="MagnifyingGlassIcon" size={22} />
            Explore Holidays
          </button>
        </div>
      </form>
    </div>
  );
};

export default HolidaySearchForm;